import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';

/* -------------------------------------------------------------------------- */
/* Afmetingen bepalen                                                          */
/* -------------------------------------------------------------------------- */

type Size = { width: number; height: number };

const IMAGE_FALLBACK: Size = { width: 1600, height: 1200 };
const VIDEO_FALLBACK: Size = { width: 1280, height: 720 };
const MEASURE_TIMEOUT = 3000;

/** Leest de echte pixelafmetingen van een afbeelding. */
const measureImage = (src: string): Promise<Size> =>
    new Promise((resolve) => {
        const img = new Image();
        const timer = window.setTimeout(() => resolve(IMAGE_FALLBACK), MEASURE_TIMEOUT);

        img.onload = () => {
            window.clearTimeout(timer);
            resolve(
                img.naturalWidth && img.naturalHeight
                    ? { width: img.naturalWidth, height: img.naturalHeight }
                    : IMAGE_FALLBACK
            );
        };

        img.onerror = () => {
            window.clearTimeout(timer);
            resolve(IMAGE_FALLBACK);
        };

        img.src = src;
    });

/** Leest de afmetingen uit de metadata van een video-element. */
const measureVideo = (video: HTMLVideoElement): Promise<Size> =>
    new Promise((resolve) => {
        const done = () => {
            window.clearTimeout(timer);
            resolve(
                video.videoWidth && video.videoHeight
                    ? { width: video.videoWidth, height: video.videoHeight }
                    : VIDEO_FALLBACK
            );
        };

        const timer = window.setTimeout(() => resolve(VIDEO_FALLBACK), MEASURE_TIMEOUT);

        if (video.readyState >= 1) {
            done();
            return;
        }

        video.addEventListener('loadedmetadata', done, { once: true });
        video.addEventListener('error', () => resolve(VIDEO_FALLBACK), { once: true });
    });

/**
 * Zorgt dat elke link data-pswp-width en data-pswp-height heeft.
 * Zonder die twee weet PhotoSwipe niet hoe ver het mag inzoomen.
 * Links waar Twig de afmetingen al heeft meegegeven worden overgeslagen.
 */
const hydrateSizes = async (gallery: HTMLElement): Promise<void> => {
    const links = Array.from(
        gallery.querySelectorAll<HTMLAnchorElement>('a.gallery-link')
    );

    await Promise.all(
        links.map(async (link) => {
            if (link.dataset.pswpWidth && link.dataset.pswpHeight) {
                return;
            }

            const type = link.dataset.fileType;
            let size: Size;

            if (type === 'video') {
                const video = link.querySelector('video');
                size = video ? await measureVideo(video) : VIDEO_FALLBACK;
            } else if (type === 'iframe') {
                const iframe = link.querySelector('iframe');
                size = {
                    width: Number(iframe?.getAttribute('width')) || VIDEO_FALLBACK.width,
                    height: Number(iframe?.getAttribute('height')) || VIDEO_FALLBACK.height
                };
                if (iframe?.src) {
                    link.dataset.pswpSrc = iframe.src;
                }
            } else {
                size = await measureImage(link.href);
            }

            link.dataset.pswpWidth = String(size.width);
            link.dataset.pswpHeight = String(size.height);
        })
    );
};

/* -------------------------------------------------------------------------- */
/* Lightbox                                                                    */
/* -------------------------------------------------------------------------- */

const initLightbox = (gallery: HTMLElement): void => {
    const lightbox = new PhotoSwipeLightbox({
        gallery,
        children: 'a.gallery-link',
        pswpModule: () => import('photoswipe'),

        // uiterlijk
        bgOpacity: 0.6,
        showHideAnimationType: 'zoom',
        zoomAnimationDuration: 300,

        // zoomgedrag
        initialZoomLevel: 'fit',
        secondaryZoomLevel: 2,   // 'fill' als je het scherm wilt vullen bij dubbelklik
        maxZoomLevel: 4,
        imageClickAction: 'zoom',
        tapAction: 'zoom',
        doubleTapAction: 'zoom',
        wheelToZoom: true,

        // sluiten
        closeOnVerticalDrag: true,
        pinchToClose: false,     // anders vecht pinch-zoom met de sluitbeweging

        paddingFn: (viewportSize) => ({
            top: viewportSize.y * 0.1,
            right: viewportSize.x * 0.1,
            bottom: viewportSize.y * 0.1,
            left: viewportSize.x * 0.1
        })
    });

    // Video's en iframes krijgen een eigen type, zodat PhotoSwipe ze niet
    // als afbeelding probeert te laden (en zoom voor die slides uitschakelt).
    lightbox.addFilter('domItemData', (itemData: any, element: HTMLAnchorElement) => {
        const type = element.dataset.fileType;

        if (type === 'video' || type === 'iframe') {
            itemData.type = type;
            itemData.src =
                element.dataset.pswpSrc ||
                element.querySelector('source')?.src ||
                element.href;
        }

        return itemData;
    });

    // Custom rendering voor video en iframe.
    lightbox.on('contentLoad', (e: any) => {
        const { content } = e;

        if (content.type !== 'video' && content.type !== 'iframe') {
            return;
        }

        e.preventDefault();

        const wrapper = document.createElement('div');
        wrapper.className = 'pswp__custom-content';

        if (content.type === 'video') {
            const video = document.createElement('video');
            video.src = content.data.src;
            video.controls = true;
            video.playsInline = true;
            video.preload = 'metadata';
            wrapper.appendChild(video);
        } else {
            const iframe = document.createElement('iframe');
            iframe.src = content.data.src;
            iframe.allow =
                'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
            iframe.allowFullscreen = true;
            iframe.frameBorder = '0';
            wrapper.appendChild(iframe);
        }

        content.element = wrapper;
    });

    // Video pauzeren zodra de slide uit beeld gaat.
    lightbox.on('contentDeactivate', (e: any) => {
        const video = e.content?.element?.querySelector?.('video');
        video?.pause();
    });

    // Thumbnail-bullets onderin.
    lightbox.on('uiRegister', () => {
        lightbox.pswp.ui.registerElement({
            name: 'bulletsIndicator',
            className: 'pswp__bullets-indicator',
            appendTo: 'wrapper',
            onInit: (el: HTMLElement, pswp: any) => {
                const total = pswp.getNumItems();

                if (total < 2) {
                    el.style.display = 'none';
                    return;
                }

                const bullets: HTMLButtonElement[] = [];
                let prevIndex = -1;

                for (let i = 0; i < total; i++) {
                    const itemData = pswp.getItemData(i);
                    const thumb =
                        itemData.element?.querySelector('img')?.src ??
                        (itemData.type === 'image' || !itemData.type ? itemData.src : '');

                    const bullet = document.createElement('button');
                    bullet.type = 'button';
                    bullet.className = 'pswp__bullet';
                    bullet.setAttribute('aria-label', `Ga naar item ${i + 1} van ${total}`);

                    if (thumb) {
                        bullet.style.backgroundImage = `url(${thumb})`;
                    }

                    bullet.onclick = () => pswp.goTo(bullets.indexOf(bullet));

                    el.appendChild(bullet);
                    bullets.push(bullet);
                }

                pswp.on('change', () => {
                    if (prevIndex >= 0) {
                        bullets[prevIndex].classList.remove('pswp__bullet--active');
                    }
                    bullets[pswp.currIndex].classList.add('pswp__bullet--active');
                    prevIndex = pswp.currIndex;
                });
            }
        });
    });

    lightbox.init();
};

/* -------------------------------------------------------------------------- */
/* Bootstrap                                                                   */
/* -------------------------------------------------------------------------- */

const boot = (): void => {
    document
        .querySelectorAll<HTMLElement>('.GalleryComponent')
        .forEach(async (gallery) => {
            if (gallery.dataset.lightboxReady === 'true') {
                return;
            }

            gallery.dataset.lightboxReady = 'true';

            await hydrateSizes(gallery);
            initLightbox(gallery);
        });
};

if (document.readyState === 'loading') {
    window.addEventListener('DOMContentLoaded', boot);
} else {
    boot();
}

export { boot as initGalleries };